import type { SnapshotBye, SnapshotGame } from '../data/snapshotTypes'

/**
 * Picking the game the scorer is about to record.
 *
 * The rule from the brief: next game by date, tolerating +/- 1 day. The
 * tolerance matters because a Wednesday night game runs past midnight if the
 * iPad clock drifts or the app is opened late, and because a game occasionally
 * gets moved a day without the scorer noticing.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days between two yyyy-mm-dd dates, ignoring time and timezone. */
export function daysBetween(fromISO: string, toISO: string): number {
  const parse = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number)
    return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)
  }
  return Math.round((parse(toISO) - parse(fromISO)) / DAY_MS)
}

/** Games we could plausibly be about to score. */
function isScoreable(game: SnapshotGame): boolean {
  return game.status !== 'CANCELLED'
}

export interface FixtureMatch {
  game: SnapshotGame
  /** Negative = in the past, 0 = today, positive = upcoming. */
  dayOffset: number
  /** True when it falls inside the +/- tolerance and should be auto-selected. */
  isTonight: boolean
}

/**
 * The fixture to preselect, or null if nothing is close enough.
 *
 * Ties are broken toward today, then toward the future -- if a game sits on
 * either side of today at equal distance, the upcoming one is the better guess.
 */
export function detectFixture(
  games: SnapshotGame[],
  todayISO: string,
  toleranceDays = 1,
): FixtureMatch | null {
  const candidates = games
    .filter(isScoreable)
    .map((game) => ({ game, dayOffset: daysBetween(todayISO, game.date) }))
    .filter((c) => Math.abs(c.dayOffset) <= toleranceDays)

  if (candidates.length === 0) return null

  candidates.sort((a, b) => {
    const byCloseness = Math.abs(a.dayOffset) - Math.abs(b.dayOffset)
    if (byCloseness !== 0) return byCloseness
    return b.dayOffset - a.dayOffset // prefer the future one
  })

  return { ...candidates[0]!, isTonight: true }
}

/** The next fixture that has not been played, for when nothing is on tonight. */
export function nextFixture(games: SnapshotGame[], todayISO: string): FixtureMatch | null {
  const upcoming = games
    .filter(isScoreable)
    .filter((g) => g.status !== 'FINAL')
    .map((game) => ({ game, dayOffset: daysBetween(todayISO, game.date) }))
    .filter((c) => c.dayOffset >= 0)
    .sort((a, b) => a.dayOffset - b.dayOffset)

  const first = upcoming[0]
  return first ? { ...first, isTonight: false } : null
}

/**
 * Ordered list for the manual picker: unplayed games first (soonest first),
 * then played ones most-recent-first, so re-scoring an old game is possible but
 * never the default.
 */
export function pickableFixtures(games: SnapshotGame[], todayISO: string): FixtureMatch[] {
  const withOffset = games.filter(isScoreable).map((game) => ({
    game,
    dayOffset: daysBetween(todayISO, game.date),
    isTonight: false,
  }))
  const unplayed = withOffset.filter((c) => c.game.status !== 'FINAL').sort((a, b) => a.dayOffset - b.dayOffset)
  const played = withOffset.filter((c) => c.game.status === 'FINAL').sort((a, b) => b.dayOffset - a.dayOffset)
  return [...unplayed, ...played]
}

/** "Tonight", "Tomorrow", "Yesterday", or a plain date. */
export function relativeDayLabel(dayOffset: number): string {
  if (dayOffset === 0) return 'Tonight'
  if (dayOffset === 1) return 'Tomorrow'
  if (dayOffset === -1) return 'Yesterday'
  if (dayOffset > 1) return `In ${dayOffset} days`
  return `${Math.abs(dayOffset)} days ago`
}

export interface ByeMatch {
  bye: SnapshotBye
  dayOffset: number
}

/**
 * A bye falling inside the same window fixture detection uses.
 *
 * This has to be asked separately, because a bye is the ABSENCE of a game: with
 * no fixture to find, detection would silently walk on to the next real game and
 * present it as though it were tonight's. Byes come from PlayHQ's round.byes,
 * so this is recorded fact rather than an inference from a gap in the fixture.
 */
export function detectBye(byes: SnapshotBye[], todayISO: string, toleranceDays = 1): ByeMatch | null {
  const candidates = byes
    .filter((b) => b.date)
    .map((bye) => ({ bye, dayOffset: daysBetween(todayISO, bye.date!) }))
    .filter((c) => Math.abs(c.dayOffset) <= toleranceDays)
    .sort((a, b) => Math.abs(a.dayOffset) - Math.abs(b.dayOffset) || b.dayOffset - a.dayOffset)
  return candidates[0] ?? null
}

/** "vs Sons Of Simon" / "@ No Shows". */
export function opponentLabel(game: SnapshotGame): string {
  const prefix = game.home_away === 'home' ? 'vs' : '@'
  return `${prefix} ${game.opponent_name ?? 'TBC'}`
}
